import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";

import api from "../api";
import { track } from "../utils/analytics";
import "../styles/MatchingFlow.css";

import DangerIcon from "../assets/modal-icon-danger.png";

export default function MatchingFlow() {
  const navigate = useNavigate();
  const { user_id } = useParams();
  const { state } = useLocation();
  const expired = state?.expired;

  const [showModal, setShowModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState("essentials");

  /* ───────── analytics: page view ───────── */
  useEffect(() => {
    track("page_view", {
      page_location: window.location.href,
      page_title: "Couples Plans",
    });
  }, []);

  /* show “trial expired” modal */
  useEffect(() => {
    if (expired) {
      setShowModal(true);
      track("trial_expired_modal_show", { account_type: "couple" });
    }
  }, [expired]);

  /* grey-out trial if already used */
  useEffect(() => {
    api
      .get("/account_status/", { params: { user_id } })
      .then((res) => {
        const plan = res.data.account_status.subscription?.plan;
        if (plan === "trial") {
          setSelectedPlan("standard"); // skip trial
          track("plan_auto_select", {
            account_type: "couple",
            plan_id: "standard",
          });
        }
      })
      .catch((err) => console.error("account_status fetch failed", err));
  }, [user_id]);

  /* ───────── handlers ───────── */

  const handlePlanChange = (e) => {
    setSelectedPlan(e.target.value);
    track("plan_change", {
      account_type: "couple",
      new_plan_id: e.target.value,
      location: "couples_plans",
    });
  };

  const handleCheckout = async () => {
    track("checkout_start", { account_type: "couple", plan_id: selectedPlan });

    try {
      const { data } = await api.post(
        "/stripe_session",
        { plan: selectedPlan },
        { params: { user_id, plan: selectedPlan, account_type: "couple" } }
      );

      if (data.status === "success" && data.checkoutUrl) {
        track("checkout_redirect", {
          account_type: "couple",
          plan_id: selectedPlan,
          checkout_url: data.checkoutUrl,
        });
        window.location.href = data.checkoutUrl;
      } else {
        track("checkout_error", {
          account_type: "couple",
          plan_id: selectedPlan,
          message: data.message || "no_checkout_url",
        });
        console.error("Failed to create a Stripe checkout session.");
      }
    } catch (error) {
      track("checkout_error", {
        account_type: "couple",
        plan_id: selectedPlan,
        message: error.message,
      });
      console.error("Error creating Stripe session:", error);
    }
  };

  const handleCancel = () => {
    track("navigation_click", {
      location: "couples_plans",
      nav_item: "Cancel",
      destination: "/signin",
    });
    navigate("/signin");
  };

  /* ───────── render ───────── */
  return (
    <>
      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <img src={DangerIcon} alt="Danger" className="danger-icon" />
            <p>
              Your free trial has expired. Please select a plan below to
              continue.
            </p>
            <button
              className="modal-button"
              onClick={() => setShowModal(false)}
            >
              OK
            </button>
          </div>
        </div>
      )}

      <div className="matching-flow-container">
        <header className="logo">
          <a
            href="/"
            onClick={() =>
              track("navigation_click", {
                location: "couples_plans",
                nav_item: "Logo",
                destination: "/",
              })
            }
          >
            BestDay2Marry
          </a>
        </header>

        <main>
          <h1>Wedding Matching Process</h1>
          <p>Select your subscription plan to get started:</p>

          <form>
            <fieldset>
              <legend className="visually-hidden">Subscription Plans</legend>

              {[
                {
                  id: "essentials",
                  label: "Essentials ($10)",
                  details: "1 location, 1 date. 1 month access.",
                },
                {
                  id: "standard",
                  label: "Standard ($50)",
                  details: "Any location, any date. 3 months access.",
                },
                {
                  id: "premium",
                  label: "Premium ($100)",
                  details: "Any location, any date. 6 months access.",
                },
                
              ].map((p) => (
                <div className="plan-option" key={p.id}>
                  <label>
                    <input
                      type="radio"
                      name="plan"
                      value={p.id}
                      checked={selectedPlan === p.id}
                      onChange={handlePlanChange}
                    />
                    <span className="plan-title">{p.label}</span>
                  </label>
                  <p className="plan-details">{p.details}</p>
                </div>
              ))}
            </fieldset>

            <button
              type="button"
              className="big-button"
              onClick={handleCheckout}
            >
              Proceed to Payment
            </button>
            <button
              type="button"
              className="small-button"
              onClick={handleCancel}
            >
              Cancel
            </button>
          </form>
        </main>
      </div>
    </>
  );
}
